import { useMemo, useState } from 'react';
import { COUNTRIES, countryDisplayName, flagEmoji, getCountry } from '../../utils/countries';
import './PhoneNumberField.css';

export default function PhoneNumberField({
  countryIso2,
  phone,
  onCountryChange,
  onPhoneChange,
  countryLabel,
  phoneLabel,
  phonePlaceholder,
  error,
  searchPlaceholder = 'Search country',
  disabled = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  const selected = getCountry(countryIso2);

  const countries = useMemo(
    () =>
      COUNTRIES.map((c) => ({ ...c, name: countryDisplayName(c.iso2) })).sort((a, b) =>
        a.name.localeCompare(b.name),
      ),
    [],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return countries;
    return countries.filter(
      (c) => c.name.toLowerCase().includes(q) || c.dialCode.includes(q),
    );
  }, [countries, query]);

  const handleSelect = (iso2) => {
    onCountryChange(iso2);
    setIsOpen(false);
    setQuery('');
  };

  const handleClose = () => {
    setIsOpen(false);
    setQuery('');
  };

  return (
    <div className="phone-field">
      <div className={`phone-field__group${error ? ' phone-field__group--invalid' : ''}`}>
        <button
          type="button"
          className="phone-field__country"
          onClick={() => setIsOpen(true)}
          disabled={disabled}
        >
          <span className="phone-field__country-text">
            <span className="phone-field__country-label">{countryLabel}</span>
            <span className="phone-field__country-value">
              {flagEmoji(selected.iso2)} {countryDisplayName(selected.iso2)} ({selected.dialCode})
            </span>
          </span>
          <span className="phone-field__chevron">›</span>
        </button>

        <input
          className="phone-field__input"
          aria-label={phoneLabel}
          type="tel"
          inputMode="numeric"
          value={phone}
          placeholder={phonePlaceholder ?? phoneLabel}
          disabled={disabled}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onChange={(e) => onPhoneChange(e.target.value.replace(/\D/g, ''))}
        />
        <div className="phone-field__outline-container">
          <div
            className={
              isFocused
                ? 'phone-field__outline phone-field__outline--focused'
                : 'phone-field__outline'
            }
          />
        </div>
      </div>
      {error ? <p className="phone-field__error">{error}</p> : null}

      {isOpen ? (
        <div className="phone-field__modal-overlay" onClick={handleClose}>
          <div className="phone-field__modal" onClick={(e) => e.stopPropagation()}>
            <div className="phone-field__search-wrap">
              <span className="phone-field__search-icon">🔍</span>
              <input
                className="phone-field__search"
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                autoFocus
              />
            </div>
            <div className="phone-field__list">
              {filtered.map((c) => (
                <button
                  type="button"
                  key={c.iso2}
                  className={`phone-field__option${
                    c.iso2 === selected.iso2 ? ' phone-field__option--active' : ''
                  }`}
                  onClick={() => handleSelect(c.iso2)}
                >
                  <span className="phone-field__option-flag">{flagEmoji(c.iso2)}</span>
                  <span className="phone-field__option-name">{c.name}</span>
                  <span className="phone-field__option-dial">{c.dialCode}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
